import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import feather from "feather-icons";
import "./LoginComponent.css";
import ErrorBoundary from "./../../ErrorBoundary";
import { getUsers } from "./../../services/servicelogic.js";

const LoginComponent = () => {
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();

  const [passwordVisible, setPasswordVisible] = useState(false);

  const iconContainerRef = useRef(null); // Ref for the icon container

  useEffect(() => {
    // Render the icons whenever showPassword changes
    renderIcons();
  }, [passwordVisible]);

  const renderIcons = () => {
    if (iconContainerRef.current) {
      iconContainerRef.current.innerHTML = ""; // Clear existing icon

      const iconName = passwordVisible ? "eye" : "eye-off";
      const icon = feather.icons[iconName].toSvg(); // Get the SVG

      iconContainerRef.current.insertAdjacentHTML("beforeend", icon);
    }
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  //TODO
  // const openWhatsAppChat = () => {
  //     window.open('https://wa.me/YourWhatsAppNumber', '_blank');
  // };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const auth_data = { username: userName, password: password };

    try {
      const responsePayload = await getUsers(auth_data);
      console.log("Raw API Response Wrapper:", responsePayload);

      // 1. SUCCESS PATH: Look for the authentication token string property
      const token =
        responsePayload?.token ||
        responsePayload?.data?.token ||
        responsePayload?.response?.data?.token;

      if (token) {
        const userData = responsePayload?.data || responsePayload;
        localStorage.setItem("authToken", token);
        navigate("/dashboard", { state: { userData } });
        return;
      }

      // 2. EXTRACTION PATH: Safely drill down into nested error layers
      const innerDataError = responsePayload?.data?.error; // Tunnels into responsePayload.data.error
      const genericError = responsePayload?.error;
      const statusCode = responsePayload?.status; // Reads the number 403

      // Convert everything to string to look for keywords anywhere in the object tree graphs
      const fullResponseString = JSON.stringify(responsePayload || "");
      console.log("Converted Response to String:", fullResponseString);

      // 3. 🟢 COMBINED FAILSAFE CONDITION VERIFICATION
      if (
        statusCode === 403 ||
        fullResponseString.includes("Access denied") ||
        fullResponseString.includes("approved staff")
      ) {
        // 🟢 THE PASSWORD WAS RIGHT! But account status lacks is_staff admin activation!
        handleStaffAccessDenied();
      } else {
        // Username or password input fields do not match data rows
        handleLoginError();
      }
    } catch (error) {
      console.error("Login Exception caught:", error);
      handleLoginError();
    }
  };

  // Triggered when Username & Password are CORRECT, but user is a normal registered account (not staff)
  const handleStaffAccessDenied = () => {
    Swal.fire({
      title: "அனுமதி மறுக்கப்பட்டது!",
      text: "உங்கள் கணக்கு இன்னும் நிர்வாகியால் (Admin) அங்கீகரிக்கப்படவில்லை. அங்கீகரிக்கப்பட்ட பணியாளர்கள் மட்டுமே உள்நுழைய முடியும்.",
      icon: "warning",
      confirmButtonText: "சரி",
    });
  };

  // Triggered on raw credential mismatch errors (401 Unauthorized / Wrong password)
  const handleLoginError = () => {
    Swal.fire({
      title: "உள்நுழைவு தோல்வி",
      text: "பயனர் பெயர் அல்லது கடவுச்சொல் தவறானது. தயவுசெய்து மீண்டும் சரிபார்க்கவும்.",
      icon: "error",
      confirmButtonText: "சரி",
      footer:
        '<button id="forgot-pw-btn" style="background:none; border:none; color:#007bff; cursor:pointer; text-decoration:underline;">கடவுச்சொல்லை மறந்துவிட்டீர்களா?</button>',
      didOpen: () => {
        const btn = document.getElementById("forgot-pw-btn");
        if (btn) {
          btn.addEventListener("click", () => {
            Swal.close();
            navigate("/forgot-password");
          });
        }
      },
    });
  };

  return (
    <>
      {
        <div className="container">
          <div className="d-flex justify-content-center">
            <Link to="/" className="brand-logo">
              <h2 className="brand-text text-primary ml-1">
                ராவணன் மக்கள் சேவை மையம்
              </h2>
            </Link>
          </div>
          <div className="d-flex flex-wrap">
            <div className="col-12 col-md-6">
              <img
                className="img-fluid"
                src="/assets/media/images/login-v2.svg"
                alt="Login Home Page"
              />
            </div>

            <div className="col-12 col-md-6 justify-content-center">
              <div className="col-12 mx-auto text-center">
                <h2 className="card-title font-weight-bold mb-1">
                  வரவேற்கிறோம் ராவணன் இ-சேவை!👋
                </h2>
                <p className="card-text mb-2">
                  உங்கள் கணக்கில் உள்நுழைந்து சேவைகளைத் தொடங்கவும்
                </p>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="row g-3 justify-content-center" id="login-form">
                  <input type="hidden" name="formname" value="email" />
                  <div className="col-12 col-md-10 col-lg-8">
                    <div className="mb-3 form-group">
                      <label htmlFor="lblPassword" className="form-label">
                        மின்னஞ்சல்
                      </label>
                      <input
                        required
                        className="form-control"
                        type="text"
                        name="email"
                        autoFocus
                        placeholder="மின்னஞ்சல்"
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-10 col-lg-8">
                    <div className="mb-3 form-group">
                      <label className="form-label">கடவுச்சொல்</label>
                      <div
                        className="password-input"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <input
                          className="form-control"
                          type={passwordVisible ? "text" : "password"}
                          required
                          name="pwd"
                          placeholder="கடவுச்சொல்"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                        <i
                          onClick={togglePasswordVisibility}
                          className="password-toggle-button"
                          aria-label={
                            passwordVisible ? "Hide password" : "Show password"
                          }
                        >
                          <span ref={iconContainerRef}> </span>
                        </i>
                      </div>
                      <div className="text-end">
                        <Link
                          to="/forgot-password"
                          style={{
                            textDecoration: "none",
                            fontSize: "0.85rem",
                            color: "#0d6efd",
                          }}
                        >
                          கடவுச்சொல்லை மறந்துவிட்டீர்களா?
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Change col-12 to col-md-8 to match the inputs above */}
                  <div className="col-12 col-md-8 mt-2">
                    {/* Login Button: Full width buttons are better for mobile UX in 2026 */}
                    <button
                      type="submit"
                      className="btn btn-primary w-100 py-2"
                    >
                      உள்நுழைய
                    </button>
                  </div>
                </div>
              </form>

              {/* Registration Link: Clean and Centered */}
              <div className="mt-4 pt-3 border-top col-12 col-md-8 mx-auto text-center">
                {/* Forgot Password: Now aligns with the right edge of the password box */}

                <p className="small text-secondary mb-0">
                  எங்கள் தளத்திற்கு புதியவரா நீங்கள்?
                  <Link
                    to="/register"
                    className="fw-bold ms-1"
                    style={{ textDecoration: "none", color: "#0d6efd" }}
                  >
                    கணக்கை உருவாக்க
                  </Link>
                </p>
              </div>
              {/* <button onClick={openWhatsAppChat}>Chat with us on WhatsApp</button> */}
            </div>
          </div>
        </div>
      }
    </>
  );
};

export default LoginComponent;
